import { BedDouble, CalendarDays, Copy, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { calendarNights, formatCalendarDate } from '@/lib/dates';
import { buildReservationPath, parseReservationIds } from '@/lib/reservation-url';
import { cn } from '@/lib/utils';

/** El resum d'una reserva que surt de la sortida (ja filtrada) d'una tool de lectura. */
export interface ReservationSummary {
  beds24Id: string;
  guestName?: string;
  checkIn?: string;
  checkOut?: string;
  room?: string;
  status?: string;
  price?: number;
}

/** Quantes targetes de reserva es pinten per tool call; la resta es diu amb un recompte. */
export const MAX_RESERVATION_CARDS = 4;

type Loose = Record<string, unknown>;

const isObject = (value: unknown): value is Loose =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const text = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
};

const amount = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
};

/** Un objecte és una reserva si té número de Beds24 i alguna cosa més que el número. */
function toReservation(value: unknown): ReservationSummary | null {
  if (!isObject(value)) return null;
  const beds24Id = text(value.beds24Id);
  // La reserva de proves local té un número que no és de xifres: lletres i guions també valen.
  if (!beds24Id || !/^[\w-]{1,40}$/.test(beds24Id)) return null;
  const room = isObject(value.room) ? value.room.name : value.room;
  const unit = isObject(value.unit) ? value.unit.name : value.unit;
  const summary: ReservationSummary = {
    beds24Id,
    guestName: text(value.guestName, value.name),
    checkIn: text(value.checkIn, value.arrival)?.slice(0, 10),
    checkOut: text(value.checkOut, value.departure)?.slice(0, 10),
    room: text(unit, room, value.roomName, value.unitName),
    status: text(value.status),
    price: amount(value.price, value.totalPrice, value.total),
  };
  return summary.guestName || summary.checkIn ? summary : null;
}

/**
 * Les reserves que hi ha a la sortida d'una tool, sense saber de quina tool és: `reservation`,
 * `reservations`, `results`, `items`, a l'arrel o dins de `data`. Sense duplicats, en l'ordre
 * en què arriben.
 */
export function reservationsFromToolOutput(output: unknown): ReservationSummary[] {
  const found = new Map<string, ReservationSummary>();
  const add = (value: unknown) => {
    const reservation = toReservation(value);
    if (reservation && !found.has(reservation.beds24Id)) found.set(reservation.beds24Id, reservation);
  };
  const scan = (container: unknown) => {
    if (!isObject(container)) return;
    add(container.reservation);
    for (const key of ['reservations', 'results', 'items', 'group', 'rooms']) {
      const list = container[key];
      if (Array.isArray(list)) list.forEach(add);
    }
  };
  scan(output);
  if (isObject(output)) scan(output.data);
  return [...found.values()];
}

/**
 * Les reserves de cada tool call d'un mateix missatge, sense repetir-ne cap entre tools: si dues
 * tools del mateix torn tornen la mateixa reserva (mateix `beds24Id`), la targeta surt només a la
 * **darrera**. Un torn que llegeix la reserva, l'edita i la torna a llegir per confirmar ha
 * d'ensenyar les dades d'ara (dates, habitació), no les d'abans del canvi. La clau és l'`id` de
 * la tool call; dins de cada tool, l'ordre és el d'arribada.
 */
export function reservationsByToolCall(
  tools: ReadonlyArray<{ id: string; output: unknown }>,
): Map<string, ReservationSummary[]> {
  const seen = new Set<string>();
  const byTool = new Map<string, ReservationSummary[]>();
  // De la darrera a la primera: la lectura més recent es queda la reserva.
  for (const tool of [...tools].reverse()) {
    const fresh = reservationsFromToolOutput(tool.output).filter(
      (reservation) => !seen.has(reservation.beds24Id),
    );
    for (const reservation of fresh) seen.add(reservation.beds24Id);
    byTool.set(tool.id, fresh);
  }
  // El mapa, en l'ordre de les tools (qui el recorre espera el d'arribada).
  return new Map(tools.map((tool) => [tool.id, byTool.get(tool.id) ?? []]));
}

const STATUS_LABELS: Record<string, string> = {
  CONFIRMED: 'Confirmada',
  PENDING: 'Pendent',
  NEW: 'Nova',
  REQUEST: 'Petició',
  CANCELLED: 'Cancel·lada',
  BLACK: 'Bloqueig',
  INQUIRY: 'Consulta',
};

export interface ReservationCardProps {
  reservation: ReservationSummary;
  className?: string;
}

/**
 * L'adreça de la fitxa d'una reserva, o `null` si el número no és dels que la ruta `/reserves`
 * sap obrir (`parseReservationIds`: un número de Beds24). Amb qualsevol altre número —una reserva
 * de proves local, per exemple— la ruta porta al calendari sense obrir cap fitxa, i val més no
 * oferir l'enllaç que fer veure que s'ha obert una altra reserva.
 */
export function reservationSheetPath(beds24Id: string): string | null {
  const ids = parseReservationIds(encodeURIComponent(beds24Id));
  return ids.length === 1 ? buildReservationPath(ids) : null;
}

/**
 * «Obre la fitxa» d'una reserva: l'enllaç a `/reserves/<número>` (`reservationSheetPath`), o res
 * si el número no és de Beds24. El fan servir la targeta de reserva i la d'una proposta de crear
 * una reserva, un cop aplicada: mateixa regla i mateixa navegació. 40 px d'alt (dit al mòbil).
 */
export function ReservationSheetLink({ beds24Id, className }: { beds24Id: string; className?: string }) {
  const sheetPath = reservationSheetPath(beds24Id);
  if (!sheetPath) return null;
  return (
    <Button asChild size="sm" variant="outline" className={cn('h-10 min-w-0 max-w-full', className)}>
      <Link to={sheetPath}>
        <ExternalLink className="size-3.5 shrink-0" />
        <span className="truncate">Obre la fitxa</span>
      </Link>
    </Button>
  );
}

/**
 * Targeta d'una reserva dins del xat, amb accions: obrir la fitxa (`/reserves/<número>`, només si
 * el número és de Beds24) i copiar el número. No porta cap dada que no hagi sortit ja del backend
 * per l'stream.
 *
 * Cap fila desborda la targeta, per estreta que sigui la columna: la targeta i els contenidors
 * són `min-w-0`, la fila d'accions fa `flex-wrap`, i cada botó pot encongir-se (`min-w-0
 * max-w-full`) tallant l'etiqueta amb punts suspensius (`truncate`) abans que sortir del marge.
 */
export function ReservationCard({ reservation, className }: ReservationCardProps) {
  const { beds24Id, guestName, checkIn, checkOut, room, status, price } = reservation;
  const nights = calendarNights(checkIn, checkOut);
  const cancelled = status?.toUpperCase() === 'CANCELLED';

  const copyId = () => {
    void navigator.clipboard
      ?.writeText(beds24Id)
      .then(() => toast.success(`Número ${beds24Id} copiat`))
      .catch(() => toast.error('No s\'ha pogut copiar'));
  };

  return (
    <div
      className={cn('min-w-0 rounded-md border bg-background p-3 text-sm', className)}
      data-testid="reservation-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={cn('truncate font-medium', cancelled && 'line-through')}>
            {guestName || 'Reserva'}
          </p>
          <p className="truncate font-mono text-muted-foreground text-xs">{beds24Id}</p>
        </div>
        {status && (
          <Badge variant={cancelled ? 'destructive' : 'secondary'} className="shrink-0 text-xs">
            {STATUS_LABELS[status.toUpperCase()] ?? status}
          </Badge>
        )}
      </div>

      <div className="mt-2 flex min-w-0 flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
        {checkIn && (
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="size-3.5" />
            {formatCalendarDate(checkIn, 'ca', 'd MMM')}
            {checkOut && ` → ${formatCalendarDate(checkOut, 'ca', 'd MMM y')}`}
            {nights > 0 && ` · ${nights} ${nights === 1 ? 'nit' : 'nits'}`}
          </span>
        )}
        {room && (
          <span className="inline-flex items-center gap-1">
            <BedDouble className="size-3.5" />
            {room}
          </span>
        )}
        {price !== undefined && <span className="font-medium text-foreground">{price.toFixed(2)} €</span>}
      </div>

      <div className="mt-3 flex min-w-0 flex-wrap gap-2" data-testid="reservation-card-actions">
        <ReservationSheetLink beds24Id={beds24Id} />
        <Button
          size="sm"
          variant="ghost"
          className="h-10 min-w-0 max-w-full"
          type="button"
          onClick={copyId}
          aria-label={`Copia el número ${beds24Id}`}
          title="Copia el número"
        >
          <Copy className="size-3.5 shrink-0" />
          <span className="truncate">Copia el número</span>
        </Button>
      </div>
    </div>
  );
}

/**
 * Les reserves d'una tool call: fins a `MAX_RESERVATION_CARDS` targetes i el recompte de la resta.
 *
 * Amb `reservations` es pinten aquelles (el missatge ja n'ha tret les que una altra tool del
 * mateix torn havia ensenyat, `reservationsByToolCall`) i el recompte surt d'elles; sense, es
 * llegeixen de `output`.
 */
export function ReservationCards({
  output,
  reservations: given,
}: {
  output?: unknown;
  reservations?: ReservationSummary[];
}) {
  const reservations = given ?? reservationsFromToolOutput(output);
  if (reservations.length === 0) return null;
  const shown = reservations.slice(0, MAX_RESERVATION_CARDS);
  const rest = reservations.length - shown.length;

  return (
    <div className="min-w-0 space-y-2">
      {/* Les columnes surten de l'amplada del xat, no de la de la finestra (`sm:` mirava la
          finestra, i amb les barres laterals obertes feia dues columnes massa estretes): tantes
          com n'hi càpiguen de 15rem, i una de sola si el xat és més estret que això. */}
      <div
        className="grid min-w-0 grid-cols-[repeat(auto-fill,minmax(min(100%,15rem),1fr))] gap-2"
        data-testid="reservation-cards-grid"
      >
        {shown.map((reservation) => (
          <ReservationCard key={reservation.beds24Id} reservation={reservation} />
        ))}
      </div>
      {rest > 0 && (
        <p className="text-muted-foreground text-xs">
          i {rest} {rest === 1 ? 'reserva més' : 'reserves més'}: demana-les a Miranda o obre el llistat.
        </p>
      )}
    </div>
  );
}
